/**
 * Patient Profile overhaul — "Needs attention", computed in the browser.
 *
 * Plan: docs/plans/2026-10-08-patient-profile-overhaul-plan.md
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * On Schedule View the panel's "Needs attention" tiles come from POST /schedule-view/alerts. That
 * service has no prod stack, so the Patient Profile page builds the SAME payload here from what it
 * has already loaded — `{ date, byPatNum: { [PatNum]: { key: detail } }, ran }` — and the panel's
 * own code (visibleAlertKeys + describeAlert in src/utils/scheduleView/alerts.js) draws it unchanged.
 * Every detail object below is the shape the server's rule produces, so the sentences match.
 *
 * ── `ran` IS THE HONESTY ─────────────────────────────────────────────────────────────────────────
 * A key is listed in `ran` only when this file could actually decide it. A key it could not decide
 * (the source has not answered yet, or the data cannot say) is left OUT — never reported as "clean".
 * Recall, unscheduled-at-a-cleaning, claims, forms, medical history and signatures need data the
 * PATIENT_PROFILE query does not carry, so they are never in `ran` here (PROFILE_ALERT_KEYS).
 *
 * ── THE RULES, EACH MIRRORING schedule-view-service/alerts/<key>.js ──────────────────────────────
 *   premed     patient.Premed === 1. "Requires" — nothing records that it was taken.
 *   balance    The FAMILY's balance (OD bills the guarantor). Decidable only when this patient IS the
 *              guarantor: the profile row's PatientAmount is then the family figure. Fires at
 *              >= $0.01 (the server's floor with the default minimum of 0); credits never fire.
 *   followup   The upcomingFollowups rows, minus stopped ones (Nilay 2026-09-20: a stopped follow-up
 *              is history, not a prompt). Types named from the server's TASK_TYPES, sorted.
 *              pastDue = a FollowupDate before today; a NULL date is normal, never "overdue".
 *   insverify  The Insurance Verification status the page already fetches: never verified, or stale
 *              by that service's own verdict (`isStale`). No visible plan ⇒ no alert (self-pay).
 *   task       Open tasks tagged with the patient. A COUNT only — task titles carry names and
 *              Firestore bypasses the PHI demo mask.
 */

/** The keys this file can ever decide, in the panel's display order (scheduleView ALERT_KEYS). */
export const PROFILE_ALERT_KEYS = ['balance', 'followup', 'premed', 'insverify', 'task'];

/** schedule-view-service/alerts/followup.js TASK_TYPES — the names the server puts in `types`. */
export const FOLLOWUP_TASK_TYPES = {
  2: 'Outstanding Claim',
  3: 'Overdue Recall',
  4: 'Unscheduled Treatment',
  5: 'Outstanding Pre-Auth',
  6: 'Processed Pre-Auth',
  7: 'Patient Balance',
  8: 'Broken Appointment',
};

/** Anything under a cent is zero (OD stores denormalized floats). */
export const BALANCE_FLOOR = 0.01;

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** 'YYYY-MM-DD…' → whole days since 1970 (UTC), or null. Digit maths: no local-time parsing of OD text. */
function dayNumber(value) {
  if (value === null || value === undefined) return null;
  const m = YMD_RE.exec(String(value));
  if (!m) return null;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isFinite(t) ? Math.round(t / 86400000) : null;
}

function isStopped(row) {
  return row && (row.FollowupStopped === 1 || row.FollowupStopped === '1' || row.FollowupStopped === true);
}

/** The balance detail, or undefined (no alert), or null (undecidable — keep 'balance' out of `ran`). */
export function balanceAlert(patient) {
  if (!patient) return null;
  // Not the guarantor: the family figure lives on someone else's row, which this page did not load.
  if (patient.Guarantor === null || patient.Guarantor === undefined) return null;
  if (Number(patient.Guarantor) !== Number(patient.PatNum)) return null;
  const amount = Number(patient.PatientAmount);
  if (!Number.isFinite(amount) || amount < BALANCE_FLOOR) return undefined;
  return {
    amount,
    guarantor: { PatNum: patient.PatNum, LName: patient.LName, FName: patient.FName },
  };
}

/** The follow-up detail, or undefined (no open follow-ups). */
export function followupAlert(rows, today) {
  const open = (Array.isArray(rows) ? rows : []).filter(r => r && !isStopped(r));
  if (!open.length) return undefined;
  const types = [];
  const todayN = dayNumber(today);
  let pastDue = false;
  open.forEach((r) => {
    const name = FOLLOWUP_TASK_TYPES[Number(r.TaskTypeID)];
    if (name && types.indexOf(name) === -1) types.push(name);
    const due = dayNumber(r.FollowupDate);
    if (due !== null && todayN !== null && due < todayN) pastDue = true;
  });
  types.sort();
  return { count: open.length, types, pastDue };
}

/** The insurance detail, or undefined (verified and fresh, or nothing to verify). */
export function insverifyAlert(insurance, today) {
  if (!insurance) return undefined;
  const plans = Array.isArray(insurance.plans) ? insurance.plans : null;
  if (plans && plans.length === 0) return undefined;
  if (insurance.neverVerified) return { neverVerified: true };
  if (!insurance.isStale) return undefined;
  const last = dayNumber(insurance.lastVerified);
  const now = dayNumber(today);
  const detail = { neverVerified: false };
  if (last !== null && now !== null) detail.ageDays = Math.max(0, now - last);
  return detail;
}

/** Open tasks (not completed, not deleted) tagged with the patient. */
export function openTaskCount(tasks) {
  return (Array.isArray(tasks) ? tasks : []).filter((t) => {
    if (!t || t.deleted === true) return false;
    return !(t.status && String(t.status).toLowerCase() === 'completed');
  }).length;
}

/**
 * Build the alerts payload for ONE patient.
 *
 * Pass `undefined` for a source that has not answered yet; its key stays out of `ran`.
 *
 * @param {object} p
 * @param {object|null} p.patient     the adapted patient row (profileAdapter), or null
 * @param {Array} [p.followups]       upcomingFollowups rows; undefined = not loaded
 * @param {object|null} [p.insurance] Insurance Verification status; undefined = not loaded,
 *                                    null = feature off / failed (never in `ran`)
 * @param {Array} [p.tasks]           tasks tagged with the patient; undefined = not loaded
 * @param {string} p.today            YYYY-MM-DD in the site's timezone
 */
export function buildProfileAlertPayload({ patient, followups, insurance, tasks, today } = {}) {
  const payload = { date: today || null, byPatNum: {}, ran: [] };
  if (!patient || patient.PatNum === null || patient.PatNum === undefined) return payload;

  const found = {};
  const ran = [];

  const balance = balanceAlert(patient);
  if (balance !== null) {
    ran.push('balance');
    if (balance) found.balance = balance;
  }

  if (Array.isArray(followups)) {
    ran.push('followup');
    const f = followupAlert(followups, today);
    if (f) found.followup = f;
  }

  ran.push('premed');
  if (Number(patient.Premed) === 1) found.premed = {};

  if (insurance) {
    ran.push('insverify');
    const i = insverifyAlert(insurance, today);
    if (i) found.insverify = i;
  }

  if (Array.isArray(tasks)) {
    ran.push('task');
    const n = openTaskCount(tasks);
    if (n > 0) found.task = { count: n };
  }

  payload.ran = PROFILE_ALERT_KEYS.filter(k => ran.indexOf(k) !== -1);
  if (Object.keys(found).length) payload.byPatNum[String(patient.PatNum)] = found;
  return payload;
}
